import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, Link } from 'react-router-dom'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import Input from '../../components/input'
import general from '../../utils/general'
import useI18n from '../../i18n/useI18n'

const defaultForm = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  phone: '',
  kvkkAccepted: true
}

const StudentCreatePage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useI18n()

  const [formData, setFormData] = useState(defaultForm)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }

  const validate = () => {
    const errs = {}
    if (!formData.firstName.trim()) errs.firstName = t('QUICK_STUDENT_MODAL_VALIDATION_REQUIRED')
    if (!formData.lastName.trim()) errs.lastName = t('QUICK_STUDENT_MODAL_VALIDATION_REQUIRED')
    if (!formData.email.trim()) errs.email = t('QUICK_STUDENT_MODAL_VALIDATION_REQUIRED')
    if (!formData.password.trim()) errs.password = t('QUICK_STUDENT_MODAL_VALIDATION_REQUIRED')
    if (!formData.phone.trim()) errs.phone = t('QUICK_STUDENT_MODAL_VALIDATION_REQUIRED')
    if (!formData.kvkkAccepted) errs.kvkkAccepted = t('QUICK_STUDENT_MODAL_VALIDATION_KVKK')
    return errs
  }

  const fieldError = (name) =>
    errors[name] ? (
      <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
        {errors[name]}
      </div>
    ) : null

  const handleSubmit = (e) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }

    setIsSubmitting(true)

    const payload = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      password: formData.password.trim(),
      phone: formData.phone.trim(),
      kvkkAccepted: true
    }

    dispatch(
      ApiCallActions.Post({
        url: 'agents/me/students',
        data: payload,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: ({ data }) => {
          const created = data?.data || data
          const studentId = created?.studentId
          general.messageSuccess(t('QUICK_STUDENT_MODAL_SUCCESS'))
          navigate(`/students/${studentId}/edit`)
        },
        onError: () => {
          setIsSubmitting(false)
        },
        callback: () => setIsSubmitting(false)
      })
    )
  }

  return (
    <section className="content">
      <div className="block-header">
        <div className="row">
          <div className="col-lg-7 col-md-6 col-sm-12 text-left">
            <h2>
              {t('QUICK_STUDENT_MODAL_TITLE')}
              <small>{t('QUICK_STUDENT_MODAL_SUBTITLE')}</small>
            </h2>
          </div>
          <div className="col-lg-5 col-md-6 col-sm-12">
            <button
              className="btn btn-white btn-icon btn-round float-right m-l-10"
              type="button"
              onClick={() => navigate('/students')}
              title={t('COMMON_ACTIONS_CANCEL')}
            >
              <i className="zmdi zmdi-arrow-left"></i>
            </button>
            <ul className="breadcrumb float-md-right">
              <li className="breadcrumb-item">
                <Link to="/dashboard">
                  <i className="zmdi zmdi-home" style={{ marginRight: '5px' }}></i>
                  {t('COMMON_HOME')}
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/students">{t('STUDENTS_LIST_TITLE')}</Link>
              </li>
              <li className="breadcrumb-item active">{t('STUDENTS_ACTIONS_NEW')}</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row clearfix">
          <div className="col-lg-12">
            <div className="card">
              <div className="body" style={{ paddingTop: '20px' }}>
                <form onSubmit={handleSubmit}>
                  <div className="row clearfix">
                    <div className="col-md-6 col-sm-12">
                      <div className="form-group">
                        <label>
                          {t('QUICK_STUDENT_MODAL_FIELD_FIRST_NAME')} <span className="text-danger">*</span>
                        </label>
                        <Input
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder={t('QUICK_STUDENT_MODAL_FIELD_FIRST_NAME')}
                        />
                        {fieldError('firstName')}
                      </div>
                    </div>
                    <div className="col-md-6 col-sm-12">
                      <div className="form-group">
                        <label>
                          {t('QUICK_STUDENT_MODAL_FIELD_LAST_NAME')} <span className="text-danger">*</span>
                        </label>
                        <Input
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder={t('QUICK_STUDENT_MODAL_FIELD_LAST_NAME')}
                        />
                        {fieldError('lastName')}
                      </div>
                    </div>
                  </div>

                  <div className="row clearfix">
                    <div className="col-md-6 col-sm-12">
                      <div className="form-group">
                        <label>
                          {t('QUICK_STUDENT_MODAL_FIELD_EMAIL')} <span className="text-danger">*</span>
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="student@example.com"
                        />
                        {fieldError('email')}
                      </div>
                    </div>
                    <div className="col-md-6 col-sm-12">
                      <div className="form-group">
                        <label>
                          {t('QUICK_STUDENT_MODAL_FIELD_PHONE')} <span className="text-danger">*</span>
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+905551234567"
                        />
                        {fieldError('phone')}
                      </div>
                    </div>
                  </div>

                  <div className="row clearfix">
                    <div className="col-md-6 col-sm-12">
                      <div className="form-group">
                        <label>
                          {t('QUICK_STUDENT_MODAL_FIELD_PASSWORD')} <span className="text-danger">*</span>
                        </label>
                        <Input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="••••••••"
                        />
                        {fieldError('password')}
                      </div>
                    </div>
                    <div className="col-md-6 col-sm-12">
                      <div className="form-group" style={{ paddingTop: '28px' }}>
                        <div className="d-flex align-items-center">
                          <input
                            type="checkbox"
                            id="kvkkAccepted"
                            name="kvkkAccepted"
                            checked={formData.kvkkAccepted}
                            onChange={handleChange}
                            style={{ marginRight: '8px' }}
                          />
                          <label htmlFor="kvkkAccepted" style={{ marginBottom: 0 }}>
                            {t('QUICK_STUDENT_MODAL_FIELD_KVKK')}
                          </label>
                        </div>
                        {fieldError('kvkkAccepted')}
                      </div>
                    </div>
                  </div>

                  <div className="row clearfix" style={{ marginTop: '10px' }}>
                    <div className="col-md-12">
                      <button
                        type="button"
                        className="btn btn-default btn-round m-r-10"
                        onClick={() => navigate('/students')}
                        disabled={isSubmitting}
                      >
                        {t('COMMON_ACTIONS_CANCEL')}
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary btn-round"
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? t('QUICK_STUDENT_MODAL_SUBMITTING')
                          : t('QUICK_STUDENT_MODAL_SUBMIT')}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default StudentCreatePage
